import { useState } from "react";
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  CheckCircle2,
  Copy,
  Check,
  AlertTriangle,
} from "lucide-react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { Modal } from "../ui/Modal";
import { Input } from "../ui/Input";
import { Select } from "../ui/Select";
import api from "../../lib/api";

export interface TeamMember {
  id: string;
  role: string; // CompanyMember role: OWNER / ADMIN / USER
  companyId?: string;
  user: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    role: string; // global user role
    isActive: boolean;
  };
}

interface TeamManagerProps {
  companyId?: string;
  members: TeamMember[];
  maxUsers: number | null; // null = onbeperkt
  ownerId: string;
  currentUserId: string;
  onReload: () => Promise<any>;
}

const MEMBER_ROLES = [
  { value: "USER", label: "Gebruiker" },
  { value: "ADMIN", label: "Beheerder" },
];

function roleLabel(r: string) {
  return r === "OWNER" ? "Eigenaar" : r === "ADMIN" ? "Beheerder" : "Gebruiker";
}

export function TeamManager({
  companyId,
  members,
  maxUsers,
  ownerId,
  currentUserId,
  onReload,
}: TeamManagerProps) {
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<
    | { type: "create" }
    | { type: "edit"; member: TeamMember }
    | { type: "delete"; member: TeamMember }
    | null
  >();

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  const count = members.length;
  const atLimit = maxUsers != null && count >= maxUsers;

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6 gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <Users size={30} />
          </div>
          <div>
            <h2 className="text-lg font-bold">Gebruikers</h2>
            <p className="text-xs text-slate-500">
              {count} {maxUsers == null ? "leden (onbeperkt)" : `van ${maxUsers} plekken`} in dit
              bedrijf
            </p>
          </div>
        </div>
        <Button onClick={() => setModal({ type: "create" })} disabled={atLimit}>
          <Plus size={27} /> Gebruiker toevoegen
        </Button>
      </div>

      {toast && (
        <div className="mb-3 flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-2.5 text-sm font-semibold">
          <CheckCircle2 size={24} /> {toast}
        </div>
      )}
      {error && (
        <div className="mb-3 flex items-center gap-2 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 px-4 py-2.5 text-sm font-semibold">
          <AlertTriangle size={24} /> {error}
        </div>
      )}

      {atLimit && (
        <div className="mb-4 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2.5 text-sm">
          <AlertTriangle size={24} className="mt-0.5 shrink-0" />
          <span>
            Je pakket-limiet is bereikt ({maxUsers} gebruikers). Upgrade je abonnement om meer
            teamleden toe te voegen.
          </span>
        </div>
      )}

      <div className="space-y-2">
        {members.map((m) => {
          const isOwner = m.user.id === ownerId;
          const fullName =
            m.user.firstName || m.user.lastName
              ? `${m.user.firstName || ""} ${m.user.lastName || ""}`.trim()
              : m.user.email;
          return (
            <div
              key={m.id}
              className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-100 bg-white/70"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-400 to-indigo-500 flex items-center justify-center text-white font-bold shrink-0">
                  {(m.user.firstName?.[0] || m.user.email?.[0] || "?").toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 truncate">{fullName}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {m.user.email}
                    {!m.user.isActive && (
                      <span className="ml-1 text-amber-600 font-semibold">• inactief</span>
                    )}
                    {m.user.id === currentUserId && (
                      <span className="ml-1 text-slate-400">( jij )</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge status={m.role === "OWNER" ? "CREDIT" : m.role === "ADMIN" ? "SENT" : "default"}>
                  {roleLabel(m.role)}
                </Badge>
                {!isOwner && (
                  <>
                    <button
                      onClick={() => setModal({ type: "edit", member: m })}
                      className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-brand-600 transition-colors"
                      title="Bewerken"
                    >
                      <Pencil size={24} />
                    </button>
                    <button
                      onClick={() => setModal({ type: "delete", member: m })}
                      className="p-2 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors"
                      title="Verwijderen"
                    >
                      <Trash2 size={24} />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
        {members.length === 0 && (
          <p className="text-center text-slate-400 py-6">Nog geen teamleden.</p>
        )}
      </div>

      <p className="mt-4 text-xs text-slate-400">
        Er moet altijd minstens één gebruiker blijven. De eigenaar kan niet direct worden
        verwijderd — wijzig eerst de eigenaar. Verwijderen van de laatste gebruiker is pas
        mogelijk bij opzegging van het abonnement.
      </p>

      {modal?.type === "create" && (
        <MemberFormModal
          companyId={companyId!}
          onClose={() => setModal(undefined)}
          onDone={async (msg, ok) => {
            if (ok) {
              await onReload();
              flash(msg);
            } else {
              setError(msg);
            }
            setModal(undefined);
          }}
        />
      )}
      {modal?.type === "edit" && (
        <MemberFormModal
          companyId={companyId!}
          member={modal.member}
          onClose={() => setModal(undefined)}
          onDone={async (msg, ok) => {
            if (ok) {
              await onReload();
              flash(msg);
            } else {
              setError(msg);
            }
            setModal(undefined);
          }}
        />
      )}
      {modal?.type === "delete" && (
        <DeleteMemberModal
          companyId={companyId!}
          member={modal.member}
          onClose={() => setModal(undefined)}
          onDone={async (msg, ok) => {
            if (ok) {
              await onReload();
              flash(msg);
            } else {
              setError(msg);
            }
            setModal(undefined);
          }}
        />
      )}
    </Card>
  );
}

/* ----------------------------- Create / Edit ----------------------------- */

interface MemberFormModalProps {
  companyId: string;
  member?: TeamMember;
  onClose: () => void;
  onDone: (msg: string, ok: boolean) => Promise<void> | void;
}

function MemberFormModal({ companyId, member, onClose, onDone }: MemberFormModalProps) {
  const isEdit = !!member;
  const [email, setEmail] = useState(member?.user.email || "");
  const [firstName, setFirstName] = useState(member?.user.firstName || "");
  const [lastName, setLastName] = useState(member?.user.lastName || "");
  const [role, setRole] = useState(member?.role === "OWNER" ? "ADMIN" : member?.role || "USER");
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!isEdit && !email.trim()) {
      setError("E-mail is verplicht");
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await api.patch(`/api/companies/${companyId}/members/${member!.user.id}`, {
          role,
          firstName,
          lastName,
        });
        await onDone("Gebruiker opgeslagen.", true);
      } else {
        const { data } = await api.post(`/api/companies/${companyId}/members`, {
          email,
          firstName,
          lastName,
          role,
        });
        if (data.generatedPassword) {
          setGeneratedPassword(data.generatedPassword);
        } else {
          await onDone("Gebruiker toegevoegd.", true);
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || "Opslaan mislukt");
    } finally {
      setLoading(false);
    }
  };

  if (generatedPassword) {
    return (
      <PasswordReveal
        password={generatedPassword}
        email={email}
        onClose={() => onDone("Gebruiker toegevoegd.", true)}
      />
    );
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? "Gebruiker bewerken" : "Gebruiker toevoegen"}
      size="md"
      footer={
        <div className="flex justify-between items-center">
          {error ? <p className="text-sm text-red-600">{error}</p> : <span />}
          <div className="flex gap-3">
            <Button variant="ghost" onClick={onClose} disabled={loading}>
              Annuleren
            </Button>
            <Button variant="primary" onClick={submit} loading={loading}>
              {isEdit ? "Opslaan" : "Toevoegen"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {!isEdit && (
          <Input
            label="E-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        )}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Voornaam"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <Input
            label="Achternaam"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>
        <Select
          label="Rol binnen dit bedrijf"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          options={MEMBER_ROLES}
        />
        {!isEdit && (
          <p className="text-xs text-slate-400">
            Er wordt een account aangemaakt (globale rol: gebruiker) en direct als lid aan dit
            bedrijf gekoppeld. Een tijdelijk wachtwoord wordt getoond om eenmalig te kopiëren.
          </p>
        )}
      </div>
    </Modal>
  );
}

/* ------------------------------- Delete ------------------------------- */

function DeleteMemberModal({
  companyId,
  member,
  onClose,
  onDone,
}: {
  companyId: string;
  member: TeamMember;
  onClose: () => void;
  onDone: (msg: string, ok: boolean) => Promise<void> | void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fullName =
    member.user.firstName || member.user.lastName
      ? `${member.user.firstName || ""} ${member.user.lastName || ""}`.trim()
      : member.user.email;

  const confirm = async () => {
    setLoading(true);
    setError("");
    try {
      await api.delete(`/api/companies/${companyId}/members/${member.user.id}`);
      await onDone("Gebruiker verwijderd.", true);
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Verwijderen mislukt";
      if (err?.response?.status === 400) {
        // Bewaar-fout (laatste lid / eigenaar) -> toon in modal
        setError(msg);
      } else {
        await onDone(msg, false);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Gebruiker verwijderen"
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Annuleren
          </Button>
          <Button variant="danger" onClick={confirm} loading={loading}>
            Verwijderen
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          Weet je zeker dat je <strong>{fullName}</strong> ({member.user.email}) uit dit bedrijf
          wilt verwijderen? Het account zelf blijft bewaard.
        </p>
        {error && (
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

/* --------------------------- Password reveal --------------------------- */

function PasswordReveal({
  password,
  email,
  onClose,
}: {
  password: string;
  email: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Gebruiker toegevoegd"
      size="sm"
      footer={
        <Button variant="primary" className="w-full" onClick={onClose}>
          Gereed
        </Button>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          Tijdelijk wachtwoord voor <strong>{email}</strong>:
        </p>
        <div className="flex items-center gap-2">
          <div className="flex-1 p-3 rounded-xl bg-slate-50 border border-slate-200 font-mono text-lg font-bold text-slate-800 break-all">
            {password}
          </div>
          <Button variant="outline" size="sm" onClick={copy}>
            {copied ? <Check size={24} /> : <Copy size={24} />}
          </Button>
        </div>
        <p className="text-xs text-slate-400">
          Dit wachtwoord is alleen nu zichtbaar. Deel het veilig met de gebruiker.
        </p>
      </div>
    </Modal>
  );
}